import { useCallback, useRef, useState } from 'react';
import { config } from '../../config/env';
import { extractApiError } from './lib/apiErrors';

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 500, 1000];

/**
 * Reports always run on the business clock (Pakistan time), not the viewer's
 * browser zone. The server pins this too; sending it keeps the two in step.
 */
export function userTimezone() {
  return config.businessTimezone;
}

/**
 * Run-state machine shared by the builder preview, the runner and the SQL workbench.
 *
 * `execute(params)` performs the request. `key` identifies the current inputs;
 * when it differs from the key of the last successful run the result is stale.
 */
export function useReportRun({ execute, key, initialPageSize = 50 }) {
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPageState] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [ranKey, setRanKey] = useState(null);
  const counter = useRef(0);

  const runWith = useCallback(
    async (nextPage, nextSize, extra = {}) => {
      const ticket = ++counter.current;
      setStatus('running');
      setError(null);
      try {
        const res = await execute({ page: nextPage, page_size: nextSize, user_timezone: userTimezone(), ...extra });
        if (ticket !== counter.current) return null;
        setResult(res.data);
        setStatus('success');
        setRanKey(key);
        return res.data;
      } catch (err) {
        if (ticket !== counter.current) return null;
        const apiErr = await extractApiError(err);
        setError(apiErr);
        setStatus('error');
        return null;
      }
    },
    [execute, key]
  );

  const run = useCallback(
    (opts = {}) => {
      const nextPage = opts.resetPage === false ? page : 1;
      if (opts.resetPage !== false) setPageState(1);
      return runWith(nextPage, pageSize, opts.extra || {});
    },
    [runWith, page, pageSize]
  );

  const setPage = useCallback(
    (nextPage, extra = {}) => {
      setPageState(nextPage);
      return runWith(nextPage, pageSize, extra);
    },
    [runWith, pageSize]
  );

  const setPageSize = useCallback(
    (nextSize, extra = {}) => {
      const size = Math.min(Number(nextSize) || initialPageSize, 1000);
      setPageSizeState(size);
      setPageState(1);
      return runWith(1, size, extra);
    },
    [runWith, initialPageSize]
  );

  const reset = useCallback(() => {
    counter.current += 1;
    setStatus('idle');
    setResult(null);
    setError(null);
    setRanKey(null);
    setPageState(1);
  }, []);

  return {
    status,
    result,
    error,
    page,
    pageSize,
    run,
    setPage,
    setPageSize,
    reset,
    isStale: status === 'success' && ranKey !== key,
    isRunning: status === 'running',
  };
}
